import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const avatarVariants = cva(
  'relative inline-flex items-center justify-center overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-xs',
        sm: 'h-8 w-8 text-sm',
        md: 'h-10 w-10 text-base',
        lg: 'h-12 w-12 text-lg',
        xl: 'h-14 w-14 text-xl',
        '2xl': 'h-16 w-16 text-2xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const avatarStatusVariants = cva(
  'absolute rounded-full border-2 border-white dark:border-gray-900',
  {
    variants: {
      status: {
        online: 'bg-success-500',
        offline: 'bg-gray-400',
        busy: 'bg-error-500',
        away: 'bg-warning-500',
      },
      size: {
        xs: 'h-1.5 w-1.5 bottom-0 right-0',
        sm: 'h-2 w-2 bottom-0 right-0',
        md: 'h-2.5 w-2.5 bottom-0.5 right-0.5',
        lg: 'h-3 w-3 bottom-0.5 right-0.5',
        xl: 'h-3.5 w-3.5 bottom-1 right-1',
        '2xl': 'h-4 w-4 bottom-1 right-1',
      },
    },
  }
);

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src?: string;
  alt?: string;
  fallback?: string;
  status?: 'online' | 'offline' | 'busy' | 'away';
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size, src, alt, fallback, status, ...props }, ref) => {
    const [imageError, setImageError] = React.useState(false);

    const initials = React.useMemo(() => {
      if (fallback) return fallback;
      if (alt) {
        return alt
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
      return '?';
    }, [fallback, alt]);

    return (
      <div
        className={cn(avatarVariants({ size, className }))}
        ref={ref}
        aria-label={alt || 'User avatar'}
        {...props}
      >
        {src && !imageError ? (
          <img
            src={src}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className="font-medium text-gray-600 dark:text-gray-400" aria-hidden="true">
            {initials}
          </span>
        )}
        {status && (
          <span
            className={cn(avatarStatusVariants({ status, size }))}
            aria-label={`Status: ${status}`}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
